import { Box } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import useColors from "../../api/hooks/useColors";
import useMSSP from "../../api/hooks/useMSSP";
import { platformUsersKeys } from "../../api/queryKeys";
import ExportButton from "../../components/buttons/ExportButton";
import SearchInput from "../../components/filters/SearchInput";
import AppPage from "../../components/structure/AppPage";
import StyledDataGrid from "../../components/StyledDataGrid";
import { Toast } from "../../components/Toast";
import { useFileNaming } from "../../hooks/useFileNaming";
import { searchObjectProperties } from "../../utils/search";
import { useLocalTime } from "../../utils/timestamp";
import AddNewTenantButton from "./AddNewTenantButton";
import AddNewTenantModal from "./AddNewTenantModal";
import { getTenantsManagementColumns } from "./TenantsManagementColumns";

export default function TenantsManagementView() {
  const colors = useColors();
  const queryClient = useQueryClient();
  const gridRef = useGridApiRef();
  const [isAddTenantModalOpen, setIsAddTenantModalOpen] = useState(false);
  const { generateFileName } = useFileNaming();
  const showLocalTime = useLocalTime();
  const {
    getMSSPTenants,
    trackEvent,
    setMSSPTenantIsPinned,
    setMSSPTenantLabels,
  } = useAPI();
  const { msspId } = useMSSP();

  const [currentTenants, setCurrentTenants] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
  });

  const { data: tenants = [], isLoading: isLoadingTenants } = useQuery({
    queryKey: platformUsersKeys.msspTenants(),
    queryFn: () => getMSSPTenants(),
    // Transform the data to ensure each row has an id and add virtual fields for sorting
    select: (response) =>
      response.data.map((tenant) => ({
        ...tenant,
        is_main_tenant: tenant.customer_id === msspId ? 1 : 0,
        id: tenant.customer_id,
        creation_date: tenant.creation_date.timestamp_utc,
      })),
  });

  const handlePinClick = async (customerId, isPinned, event) => {
    event.stopPropagation();

    try {
      await setMSSPTenantIsPinned(customerId, !isPinned);
      queryClient.invalidateQueries({
        queryKey: platformUsersKeys.msspTenants(),
      });
      trackEvent(
        `User ${isPinned ? "unpinned" : "pinned"} tenant ${customerId}`
      );
    } catch (error) {
      Toast.error(`Failed to ${isPinned ? "unpin" : "pin"} tenant`);
    }
  };

  const handleLabelDelete = async (tenantId, currentLabels, labelToDelete) => {
    try {
      const updatedLabels = currentLabels.filter(
        (label) => label !== labelToDelete
      );
      await setMSSPTenantLabels(tenantId, updatedLabels);
      queryClient.invalidateQueries({
        queryKey: platformUsersKeys.msspTenants(),
      });
      trackEvent(`User deleted label ${labelToDelete} from tenant ${tenantId}`);
    } catch (error) {
      Toast.error(`Failed to delete label ${labelToDelete}`);
    }
  };

  const handleTableExport = () => {
    const message =
      "User click on 'Export' button to download the tenants table";
    trackEvent(message);
    const csvExportOptions = {
      fileName: generateFileName(`tenants`),
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  useEffect(() => {
    trackEvent(`user change filters to ${JSON.stringify(appliedFilters)}`);

    if (currentTenants && tenants) {
      let filteredRows = tenants;

      if (appliedFilters.searchString !== "") {
        const searchString = appliedFilters.searchString;
        filteredRows = filteredRows.filter((row) => {
          return searchObjectProperties(row, searchString.toLowerCase());
        });
      }
      setCurrentTenants(filteredRows);
    }
  }, [appliedFilters, tenants]);

  const columns = getTenantsManagementColumns(
    colors,
    msspId,
    handlePinClick,
    handleLabelDelete,
    showLocalTime
  );

  return (
    <AppPage title="Tenants Management">
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" flexDirection="row" gap={2}>
          {/* Search Bar */}
          <Box sx={{ width: "300px" }}>
            <SearchInput
              fieldName={"searchString"}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={1}
            sx={{ marginLeft: "auto" }}
          >
            {/* Add New Tenant Button */}
            <Box sx={{ width: "150px" }}>
              <AddNewTenantButton
                onClick={() => {
                  setIsAddTenantModalOpen(true);
                }}
                labelVariant="overline"
                sx={{
                  border: `1px solid ${colors.button_outlined_inherit_enabled_stroke}`,
                  borderRadius: "10px",
                  marginLeft: "auto",
                }}
              />
            </Box>
            {/* Export Button */}
            <ExportButton handleExport={handleTableExport} />
          </Box>
        </Box>

        {/* Table */}
        <StyledDataGrid
          apiRef={gridRef}
          columns={columns}
          rows={currentTenants}
          loading={isLoadingTenants}
          pageSizeOptionsOverride={[5, 10, 20]}
          autoHeight
          disableRowSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [
                { field: "is_main_tenant", sort: "desc" },
                { field: "is_pinned", sort: "desc" },
                { field: "importance", sort: "desc" },
              ],
            },
            columns: {
              columnVisibilityModel: {
                is_main_tenant: false,
              },
            },
          }}
          sx={{
            cursor: "pointer",
            "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
              {
                paddingLeft: "16px",
              },
          }}
        />
      </Box>

      <AddNewTenantModal
        open={isAddTenantModalOpen}
        onClose={() => setIsAddTenantModalOpen(false)}
      />
    </AppPage>
  );
}
